import { ClipboardDocumentIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  formatPhoneNumber,
  normalizePhoneWithPlusPrefix,
} from "@/core/utils/phone";

interface ContactInfoItemProps {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
  variant?: "default" | "compact";
  iconSize?: "sm" | "xs";
}

const ContactInfoItem = ({
  label,
  value,
  onCopy,
  copied,
  variant = "default",
  iconSize = "sm",
}: ContactInfoItemProps) => (
  <Stack
    direction="row"
    align="center"
    justify="between"
    fullWidth
    className={
      variant === "default" ? "rounded-md border bg-background p-3" : "w-full"
    }
  >
    <Stack gap="xs">
      <Typography
        variant={variant === "default" ? "subtitleW1" : "captionW1"}
        className={variant === "default" ? "text-xs text-secondary-text" : ""}
      >
        {variant === "default" ? (
          label
        ) : (
          <>
            <span className="font-medium">{label}:</span> {value}
          </>
        )}
      </Typography>
      {variant === "default" && (
        <Typography variant="bodyW1">{value}</Typography>
      )}
    </Stack>
    <Button variant="ghost" size="sm" onClick={onCopy}>
      <ClipboardDocumentIcon
        className={iconSize === "sm" ? "h-4 w-4" : "h-3 w-3"}
      />
      {variant === "default" && (copied ? "Copied!" : "Copy")}
    </Button>
  </Stack>
);

export default function AdvocateSignupRedirect() {
  const [searchParams] = useSearchParams();
  const [shouldAutoRedirect, setShouldAutoRedirect] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [copied, setCopied] = useState<"phone" | "message" | null>(null);

  const phoneNumber = searchParams.get("phone") || "";
  const referralCode = searchParams.get("code") || "";

  const smsPhone = useMemo(
    () => normalizePhoneWithPlusPrefix(phoneNumber),
    [phoneNumber],
  );

  const displayPhone = useMemo(
    () => formatPhoneNumber(smsPhone, { withCountryCode: true }) || smsPhone,
    [smsPhone],
  );

  const smsBody = useMemo(() => `Referral${referralCode}`, [referralCode]);
  const smsLink = useMemo(
    () => `sms:${smsPhone}?body=${encodeURIComponent(smsBody)}`,
    [smsPhone, smsBody],
  );

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  const handleSmsClick = () => {
    setIsRedirecting(true);

    // Use setTimeout to detect if user cancelled the redirect
    const redirectTimer = setTimeout(() => {
      // If we reach here, likely means user cancelled or SMS app didn't open
      setIsRedirecting(false);
      setShowFallback(true);
    }, 1000);

    // Attempt the redirect
    try {
      window.location.href = smsLink;

      // If successful, clear the timer and we don't need to show fallback
      window.addEventListener("beforeunload", () => {
        clearTimeout(redirectTimer);
      });

      // Also check if window loses focus (app opened successfully)
      const handleBlur = () => {
        clearTimeout(redirectTimer);
        window.removeEventListener("blur", handleBlur);
      };

      window.addEventListener("blur", handleBlur);

      // Cleanup after 3 seconds regardless
      setTimeout(() => {
        clearTimeout(redirectTimer);
        window.removeEventListener("blur", handleBlur);
      }, 3000);
    } catch {
      clearTimeout(redirectTimer);
      setIsRedirecting(false);
      setShowFallback(true);
    }
  };

  useEffect(() => {
    if (isMobile && !isStandalone && phoneNumber && referralCode) {
      setShouldAutoRedirect(true);
      // Redirect immediately when data is available
      handleSmsClick();
    }
  }, [isMobile, isStandalone, phoneNumber, referralCode, smsLink]);

  const copyToClipboard = async (text: string, type: "phone" | "message") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!phoneNumber || !referralCode) {
    return (
      <Stack fullWidth align="center" justify="center" className="h-full p-4">
        <Stack gap="md" align="center">
          <Typography variant="subtitleW1" className="text-4xl">
            Invalid Link
          </Typography>
          <Typography textColor="secondary" variant="bodyW1">
            This referral link is missing required information.
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack fullWidth align="center" justify="center" className="h-full p-4">
      <Stack gap="lg" align="center" className="w-full max-w-md">
        <Stack gap="md" align="center">
          <Typography variant="headlineW3" className="text-center">
            Join with Referral Code
          </Typography>
          {isRedirecting ? (
            <Typography
              variant="bodyW1"
              className="text-center text-secondary-text"
            >
              Opening your messaging app...
            </Typography>
          ) : shouldAutoRedirect && !showFallback ? (
            <Typography
              variant="bodyW1"
              className="text-center text-secondary-text"
            >
              Opening your messaging app...
            </Typography>
          ) : (
            <Typography
              variant="bodyW1"
              className="text-center text-secondary-text"
            >
              {isMobile
                ? "Tap the button below to send your referral code"
                : "Send a text message to complete your referral sign-up"}
            </Typography>
          )}
        </Stack>

        {isMobile && (showFallback || !shouldAutoRedirect) ? (
          <Stack gap="md" align="center" fullWidth>
            <Button
              onClick={handleSmsClick}
              size="lg"
              className="w-full max-w-xs"
              loading={isRedirecting}
              leftIcon={<PhoneIcon className="h-5 w-5" />}
            >
              Send Referral Code
            </Button>
          </Stack>
        ) : !isMobile ? (
          <Stack
            gap="lg"
            fullWidth
            className="rounded-lg bg-secondary/5 p-6 pt-0"
          >
            <Stack gap="sm" fullWidth>
              <ContactInfoItem
                label="Phone Number"
                value={displayPhone}
                onCopy={() => copyToClipboard(smsPhone, "phone")}
                copied={copied === "phone"}
                variant="default"
                iconSize="sm"
              />
              <ContactInfoItem
                label="Message"
                value={smsBody}
                onCopy={() => copyToClipboard(smsBody, "message")}
                copied={copied === "message"}
                variant="default"
                iconSize="sm"
              />
            </Stack>
            <Stack gap="sm" fullWidth>
              <Button
                onClick={handleSmsClick}
                variant="secondary"
                className="w-full"
              >
                <PhoneIcon className="mr-2 h-4 w-4" />
                Try SMS Link Anyway
              </Button>
              <Typography
                variant="captionW1"
                className="text-center text-secondary-text"
              >
                SMS links may not work on all desktop browsers
              </Typography>
            </Stack>
          </Stack>
        ) : null}

        {isMobile && showFallback && (
          <Stack
            gap="sm"
            align="center"
            className="rounded-lg bg-amber-50 p-4 dark:bg-amber-950/20"
          >
            <Typography
              variant="captionW1"
              className="text-amber-700 dark:text-amber-400"
            >
              Didn't work? Try copying the details manually:
            </Typography>
            <Stack gap="xs" className="w-full">
              <ContactInfoItem
                label="Phone"
                value={displayPhone}
                onCopy={() => copyToClipboard(smsPhone, "phone")}
                copied={copied === "phone"}
                variant="compact"
                iconSize="xs"
              />
              <ContactInfoItem
                label="Message"
                value={smsBody}
                onCopy={() => copyToClipboard(smsBody, "message")}
                copied={copied === "message"}
                variant="compact"
                iconSize="xs"
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
